import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatSAR } from "../lib/api";
import { SAUDI_CITIES } from "../data/locations";

// iter240 — Fast id → {ar, en} lookup for the City column / filter.
const CITY_LABEL = Object.fromEntries(
  SAUDI_CITIES.map(([id, en, ar]) => [id, { en, ar }])
);
import {
  Shield, Users, Car, Gavel, MessageSquareWarning, CreditCard, Loader2,
  CheckCircle2, XCircle, Star, Trash2, RefreshCw, Sparkles,
  TrendingUp, Flag, Settings as SettingsIcon, Megaphone, Package, ScrollText,
  Tag, ShieldCheck, Image as ImageIcon, Wallet, Database, Gift, Megaphone as MegaphoneIcon,
  MessageSquare, MapPin, ArrowUp, ArrowDown, X, Save, ImagePlus, Wrench, FileSignature,
} from "lucide-react";

import AnalyticsTab from "./admin/AnalyticsTab";
import ReportsTab from "./admin/ReportsTab";
import SettingsTab from "./admin/SettingsTab";
import BroadcastsTab from "./admin/BroadcastsTab";
import PackagesTab from "./admin/PackagesTab";
import AuditTab from "./admin/AuditTab";
import BackupsTab from "./admin/BackupsTab";
import MakesTab from "./admin/MakesTab";
import AdminsTab from "./admin/AdminsTab";
import BannersTab from "./admin/BannersTab";
import PayoutsTab from "./admin/PayoutsTab";
import AdCampaignsTab from "./admin/AdCampaignsTab";
import PartnersTab from "./admin/PartnersTab";
import OtherServicesTab from "./admin/OtherServicesTab";
import KycTab from "./admin/KycTab";
import BusinessVerificationTab from "./admin/BusinessVerificationTab";
import DisputesTab from "./admin/DisputesTab";
import MessagesTab from "./admin/MessagesTab";
import FaqAdminTab from "./admin/FaqAdminTab";
import PrivateOffersTab from "./admin/PrivateOffersTab";
import CampaignsManagerTab from "./admin/CampaignsManagerTab";
import OffersSubscriptionsTab from "./admin/OffersSubscriptionsTab";
import VerificationAuditLogTab from "./admin/VerificationAuditLogTab";
import CartalChannelTab from "./admin/CartalChannelTab";
import IndividualArchiveTab from "./admin/IndividualArchiveTab";
import BusinessArchiveTab from "./admin/BusinessArchiveTab";
import GeoSpreadTab from "./admin/GeoSpreadTab";
import HeavyTaxonomyTab from "./admin/HeavyTaxonomyTab";
import { hasPerm } from "../lib/permissions";
import { ImportRequestAlert } from "./admin/ImportRequestAlert";
import { PaymentsCleanupPanel } from "./admin/PaymentsCleanupPanel";
import ImportAuthorizationsTab from "./admin/ImportAuthorizationsTab";
import { PAY_STATUS } from "./admin/PaymentsAdminPage";

const TABS = [
  { id: "overview", icon: Sparkles, en: "Overview", ar: "نظرة عامة" },
  { id: "analytics", icon: TrendingUp, en: "Analytics", ar: "تحليلات", perm: "admin.analytics.view" },
  { id: "users", icon: Users, en: "Users", ar: "المستخدمون", perm: "users.view" },
  { id: "vehicles", icon: Car, en: "Vehicles", ar: "المركبات", perm: "listings.vehicles.manage" },
  { id: "reports", icon: Flag, en: "Reports", ar: "البلاغات", perm: "listings.reports.manage" },
  { id: "kyc", icon: ShieldCheck, en: "KYC", ar: "التحقق (KYC)", perm: "users.verify" },
  { id: "biz_verif", icon: ShieldCheck, en: "Business Verify", ar: "توثيق الأعمال", perm: "users.business_verify" },
  { id: "disputes", icon: MessageSquareWarning, en: "Disputes", ar: "النزاعات", perm: "listings.disputes.resolve" },
  { id: "messages", icon: MessageSquareWarning, en: "Messages", ar: "الرسائل" },
  { id: "cartal_channel", icon: MessageSquare, en: "CARTAL Channel", ar: "قناة CARTAL الرسمية", superOnly: true },
  { id: "faq", icon: MessageSquareWarning, en: "FAQ Center", ar: "الأسئلة الشائعة", perm: "marketing.content.manage" },
  { id: "broadcasts", icon: Megaphone, en: "Broadcasts", ar: "إشعارات", perm: "marketing.notifications.send" },
  { id: "ads", icon: Megaphone, en: "Ad Campaigns", ar: "حملات إعلانية", perm: "marketing.content.manage" },
  { id: "partners", icon: ShieldCheck, en: "Partners", ar: "الشركاء", perm: "listings.activities.manage" },
  { id: "other_services", icon: Package, en: "Other Services", ar: "خدمات أخرى", perm: "listings.activities.manage" },
  { id: "packages", icon: Package, en: "Packages", ar: "الباقات", perm: "subscriptions.plans.manage" },
  { id: "private_offers", icon: Gift, en: "Private Offers", ar: "العروض الخاصة", superOnly: true, perm: "subscriptions.private_offers.manage" },
  { id: "campaigns", icon: MegaphoneIcon, en: "Campaigns", ar: "الحملات والعروض", superOnly: true, perm: "subscriptions.campaigns.manage" },
  { id: "offers_subs", icon: Gift, en: "Offers & Subscriptions", ar: "العروض والاشتراكات", superOnly: true, perm: "subscriptions.private_offers.manage" },
  { id: "verif_audit", icon: ShieldCheck, en: "Verification Audit Log", ar: "سجل توثيق الحسابات", superOnly: true },
  { id: "banners", icon: ImageIcon, en: "Banners", ar: "البنرات", perm: "marketing.banners.manage" },
  { id: "makes", icon: Tag, en: "Makes", ar: "الماركات", perm: "listings.vehicles.manage" },
  { id: "imports", icon: FileSignature, en: "Import Authorizations", ar: "تفويضات الاستيراد", perm: "listings.imports.manage" },
  { id: "payments", icon: CreditCard, en: "Payments", ar: "المدفوعات", perm: "finance.payments.view" },
  { id: "payouts", icon: Wallet, en: "Payouts", ar: "تحويلات", perm: "finance.transfers.approve" },
  { id: "leads", icon: Gavel, en: "Leads", ar: "الفُرص" },
  { id: "admins", icon: ShieldCheck, en: "Admins", ar: "المسؤولون", superOnly: true, perm: "admin.accounts.manage" },
  { id: "settings", icon: SettingsIcon, en: "Settings", ar: "إعدادات", superOnly: true, perm: "admin.settings.manage" },
  { id: "backups", icon: Database, en: "Backups", ar: "النسخ الاحتياطي", perm: "admin.settings.manage" },
  { id: "audit", icon: ScrollText, en: "Audit", ar: "سجل", perm: "admin.audit.view" },
  { id: "archive_individuals", icon: Users, en: "Individuals Archive", ar: "أرشيف الأفراد", superOnly: true },
  { id: "archive_businesses", icon: ShieldCheck, en: "Businesses Archive", ar: "أرشيف الأعمال", superOnly: true },
  { id: "geo_spread", icon: MapPin, en: "Geo Spread", ar: "الانتشار الجغرافي", superOnly: true },
  { id: "heavy_taxonomy", icon: Wrench, en: "Heavy Equipment (Admin)", ar: "معدات ثقيلة (إدارة)", superOnly: true },
];

function StatCard({ label, value, sub }) {
  return (
    <div className="surface rounded-xl p-5">
      <div className="text-xs text-[#6b6b6b] uppercase tracking-widest">{label}</div>
      <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{value}</div>
      {sub && <div className="text-xs text-[#8a6f2e] mt-1">{sub}</div>}
    </div>
  );
}

function Overview() {
  const { lang } = useI18n();
  const [s, setS] = useState(null);
  const reload = useCallback(() => apiClient.get("/admin/overview").then(({ data }) => setS(data)), []);
  useEffect(() => { reload(); }, [reload]);
  if (!s) return <div className="p-10 text-[#525252]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return (
    <div data-testid="admin-overview">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl">{lang === "ar" ? "نظرة عامة" : "Overview"}</h2>
        <button onClick={reload} className="btn-ghost text-sm"><RefreshCw className="w-3.5 h-3.5" /> {lang === "ar" ? "تحديث" : "Refresh"}</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={lang === "ar" ? "المستخدمون" : "Users"} value={s.users} sub={`${s.dealers} ${lang === "ar" ? "تاجر" : "dealers"}`} />
        <StatCard label={lang === "ar" ? "المركبات" : "Vehicles"} value={s.vehicles} sub={`${s.featured} ${lang === "ar" ? "مميزة" : "featured"}`} />
        <StatCard label={lang === "ar" ? "المزادات" : "Auctions"} value={s.auctions} />
        <StatCard label={lang === "ar" ? "المحادثات" : "Conversations"} value={s.conversations} />
        <StatCard label={lang === "ar" ? "النزاعات" : "Disputes"} value={s.disputes} sub={`${s.open_disputes} ${lang === "ar" ? "مفتوحة" : "open"}`} />
        <StatCard label={lang === "ar" ? "طلبات تمويل" : "Finance leads"} value={s.finance_leads} />
        <StatCard label={lang === "ar" ? "طلبات تأمين" : "Insurance leads"} value={s.insurance_leads} />
        <StatCard label={lang === "ar" ? "إيرادات (SAR)" : "Revenue (SAR)"} value={formatSAR(s.revenue_sar)} />
      </div>
    </div>
  );
}

function UsersTab() {
  const { lang } = useI18n();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [cityFilter, setCityFilter] = useState("");
  const load = useCallback(() => {
    const params = { q };
    if (cityFilter) params.city = cityFilter;
    return apiClient.get("/admin/users", { params }).then(({ data }) => setItems(data.items || []));
  }, [q, cityFilter]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    await apiClient.patch(`/admin/users/${id}`, patch);
    load();
  };
  // iter218 — verification toggle uses the dedicated endpoint under
  // /admin/verifications/user/... which is gated by the narrower
  // `users.verify` permission (rather than `users.manage`). Suspend
  // and other user edits still go through PATCH /admin/users/{id}.
  const toggleVerified = async (id, currentlyVerified) => {
    const path = currentlyVerified
      ? `/admin/verifications/user/${id}/revoke`
      : `/admin/verifications/user/${id}`;
    await apiClient.post(path);
    load();
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h2 className="font-display text-2xl">{lang === "ar" ? "المستخدمون" : "Users"} ({items.length})</h2>
        <div className="flex items-center gap-2">
          {/* iter240 — filter by city (uses the unified SAUDI_CITIES list). */}
          <select
            className="input-dark text-xs py-2"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            data-testid="admin-users-city-filter"
          >
            <option value="">{lang === "ar" ? "كل المدن" : "All cities"}</option>
            {SAUDI_CITIES.map(([id, en, ar]) => (
              <option key={id} value={id}>{lang === "ar" ? ar : en}</option>
            ))}
          </select>
          <input className="input-dark max-w-xs" placeholder={lang === "ar" ? "بحث…" : "Search…"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" />
        </div>
      </div>
      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3 text-start">{lang === "ar" ? "الاسم" : "Name"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "البريد" : "Email"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "المدينة" : "City"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الدور" : "Role"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الحالة" : "Status"}</th>
              <th className="p-3 text-end">{lang === "ar" ? "إجراء" : "Action"}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-[#0a0a0a]/[0.06]" data-testid={`admin-user-${u.id}`}>
                <td className="p-3">{u.name}{u.company_name && <span className="text-[#8a8a8a] block text-xs">{u.company_name}</span>}</td>
                <td className="p-3 text-[#3a3a3a]">{u.email}</td>
                <td className="p-3 text-[#3a3a3a]" data-testid={`admin-user-city-${u.id}`}>
                  {u.city
                    ? (CITY_LABEL[u.city] ? (lang === "ar" ? CITY_LABEL[u.city].ar : CITY_LABEL[u.city].en) : u.city)
                    : <span className="text-[#a0a0a0]">{lang === "ar" ? "غير محدَّدة" : "Not set"}</span>}
                </td>
                <td className="p-3"><span className="pill text-[10px]">{u.role}</span></td>
                <td className="p-3">
                  {u.suspended ? <span className="pill text-[10px] text-red-700 border-red-300">{lang === "ar" ? "موقوف" : "Suspended"}</span> :
                   u.verified ? <span className="pill pill-accent text-[10px]">{lang === "ar" ? "موثّق" : "Verified"}</span> :
                   <span className="pill text-[10px]">{lang === "ar" ? "قيد المراجعة" : "Pending"}</span>}
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => toggleVerified(u.id, u.verified)} className="btn-ghost text-xs" data-testid={`admin-user-verify-${u.id}`}>
                      {u.verified ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                      {u.verified ? (lang === "ar" ? "إلغاء التوثيق" : "Unverify") : (lang === "ar" ? "وثّق" : "Verify")}
                    </button>
                    <button onClick={() => update(u.id, { suspended: !u.suspended })} className="btn-ghost text-xs" data-testid={`admin-user-suspend-${u.id}`}>
                      {u.suspended ? (lang === "ar" ? "إلغاء الإيقاف" : "Unsuspend") : (lang === "ar" ? "أوقف" : "Suspend")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VehiclesTab() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [mediaVehicle, setMediaVehicle] = useState(null);
  const load = useCallback(() => apiClient.get("/admin/vehicles", { params: { q } }).then(({ data }) => setItems(data.items || [])), [q]);
  useEffect(() => { load(); }, [load]);

  // iter278 — permission gate for the "Manage Listing Media" button
  // Super admin always passes; other admins need `listings.media.manage`
  // explicitly granted. Never renders the button for unauthorised users.
  const canManageMedia = (() => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    const perms = user.permissions || [];
    return perms.includes("listings.media.manage");
  })();

  const toggleFeatured = async (v) => {
    await apiClient.patch(`/admin/vehicles/${v.id}`, { featured: !v.featured });
    load();
  };
  const endBoost = async (v) => {
    if (!window.confirm(`إنهاء التعزيز المنتهي للإعلان ${v.ad_number || v.id} وإرسال إشعار التجديد للمعلن؟`)) return;
    try {
      await apiClient.post(`/admin/vehicles/${v.id}/end-boost`);
      window.alert("تم إنهاء التعزيز وإرسال الإشعار للمعلن");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "تعذر تنفيذ العملية");
    }
    load();
  };
  const toggleWatermark = async (v) => {
    // Turning it on backfills this listing's existing images server-side
    // (see server.py's admin_update_vehicle); turning it off only flips
    // the flag and does not un-watermark already-stamped photos.
    const { data } = await apiClient.patch(`/admin/vehicles/${v.id}`, { watermark_enabled: !v.watermark_enabled });
    const result = data?._watermark_backfill;
    if (result) {
      const detail =
        `found=${result.image_urls_found} processed=${result.processed} skipped=${result.skipped} ` +
        `not_ours=${result.not_ours} failed=${result.failed.length}` +
        (result.failed.length ? ` | error: ${result.failed[0].error}` : "");
      window.alert(detail);
    }
    load();
  };
  const del = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف المركبة؟" : "Delete vehicle?")) return;
    await apiClient.delete(`/admin/vehicles/${id}`);
    load();
  };

  return (
    <div data-testid="admin-vehicles">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-2xl">{lang === "ar" ? "المركبات" : "Vehicles"} ({items.length})</h2>
        <input className="input-dark max-w-xs" placeholder={lang === "ar" ? "بحث…" : "Search…"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-vehicles-search" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((v) => (
          <div key={v.id} className="surface rounded-xl overflow-hidden" data-testid={`admin-vehicle-${v.id}`}>
            <img src={v.image} alt="" className="w-full h-32 object-cover" />
            <div className="p-3">
              <div className="font-display text-base">{v.year} {v.make} {v.model}</div>
              <div className="text-xs text-[#6b6b6b]">{v.city} · {v.type} · {formatSAR(v.price_sar)} SAR</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => toggleFeatured(v)} className={`btn-ghost text-xs ${v.featured ? "border-[#c9a55c] text-[#8a6f2e]" : ""}`} data-testid={`admin-vehicle-feature-${v.id}`}>
                  <Star className={`w-3 h-3 ${v.featured ? "fill-current" : ""}`} />
                  {v.featured ? (lang === "ar" ? "إلغاء التمييز" : "Unfeature") : (lang === "ar" ? "تمييز" : "Feature")}
                </button>
                {v.last_boost_payment_id && v.boosted_until && v.boosted_until < new Date().toISOString() && v.boost_expiry_notified_payment !== v.last_boost_payment_id && (
                  <button onClick={() => endBoost(v)} className="btn-ghost text-xs border-[#c9a55c] text-[#8a6f2e]" data-testid={`admin-vehicle-end-boost-${v.id}`}>
                    {lang === "ar" ? "إنهاء التعزيز وإشعار المعلن" : "End boost & notify"}
                  </button>
                )}
                <button onClick={() => toggleWatermark(v)} className={`btn-ghost text-xs ${v.watermark_enabled ? "border-[#c9a55c] text-[#8a6f2e]" : ""}`} data-testid={`admin-vehicle-watermark-${v.id}`}>
                  <ShieldCheck className={`w-3 h-3 ${v.watermark_enabled ? "fill-current" : ""}`} />
                  {v.watermark_enabled ? (lang === "ar" ? "العلامة المائية مفعّلة" : "Watermark On") : (lang === "ar" ? "تفعيل العلامة المائية" : "Enable Watermark")}
                </button>
                {canManageMedia && (
                  <button onClick={() => setMediaVehicle(v)} className="btn-ghost text-xs" data-testid={`admin-vehicle-media-${v.id}`}>
                    <ImageIcon className="w-3 h-3" /> {lang === "ar" ? "الصور" : "Photos"}
                  </button>
                )}
                <button onClick={() => del(v.id)} className="btn-ghost text-xs text-red-700 border-red-300" data-testid={`admin-vehicle-delete-${v.id}`}>
                  <Trash2 className="w-3 h-3" /> {lang === "ar" ? "حذف" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {mediaVehicle && (
        <ListingMediaModal
          vehicle={mediaVehicle}
          lang={lang}
          onClose={() => setMediaVehicle(null)}
          onSaved={() => { setMediaVehicle(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── iter278 — Listing photo manager ────────────────────────────────
// Kept intentionally inline as a small local component so this whole
// feature fits inside the "surgical minimal change" constraint. Uses
// up/down arrow buttons instead of a drag-and-drop library — same
// user experience without adding a new dependency, and works cleanly
// in RTL and on mobile viewports.
function ListingMediaModal({ vehicle, lang, onClose, onSaved }) {
  // Seed from the current vehicle document. We prefer `images` (the
  // authoritative array) and fall back to a single-element list built
  // from `image` when a very old listing has no images field.
  const initial = React.useMemo(() => {
    const arr = Array.isArray(vehicle.images) && vehicle.images.length
      ? vehicle.images.slice()
      : (vehicle.image ? [vehicle.image] : []);
    // Dedupe while preserving order (mirrors backend).
    const seen = new Set();
    return arr.filter((u) => u && !seen.has(u) && seen.add(u));
  }, [vehicle]);

  const [urls, setUrls] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const move = (idx, delta) => {
    setUrls((cur) => {
      const to = idx + delta;
      if (to < 0 || to >= cur.length) return cur;
      const next = cur.slice();
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };
  const setPrimary = (idx) => {
    if (idx === 0) return;
    setUrls((cur) => [cur[idx], ...cur.filter((_, i) => i !== idx)]);
  };
  const remove = (idx) => {
    setUrls((cur) => cur.filter((_, i) => i !== idx));
  };
  const reset = () => setUrls(initial);

  const save = async () => {
    if (urls.length === 0) {
      setErr(lang === "ar" ? "يجب إبقاء صورة واحدة على الأقل." : "Keep at least one photo.");
      return;
    }
    setSaving(true); setErr(null);
    try {
      await apiClient.patch(`/admin/listing-media/${vehicle.id}`, { images: urls });
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr((typeof d === "object" && (d[lang] || d.ar || d.en)) || (typeof d === "string" && d) || "Save failed.");
    } finally { setSaving(false); }
  };

  const dirty = urls.length !== initial.length || urls.some((u, i) => u !== initial[i]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-[#f1ecdf] rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="listing-media-modal">
        <div className="flex items-center justify-between p-4 border-b border-[#0a0a0a]/[0.08] sticky top-0 bg-[#f1ecdf] z-10">
          <div>
            <h3 className="font-display text-lg">
              {lang === "ar" ? "صور الإعلان" : "Listing photos"}
            </h3>
            <div className="text-[11px] text-[#8a8a8a] mt-0.5 font-mono">
              {vehicle.year} {vehicle.make} {vehicle.model} · {vehicle.id?.slice(0, 8)}
            </div>
          </div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0a0a0a]" data-testid="media-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {err && <div className="text-sm text-red-700 surface rounded-lg p-3" data-testid="media-error">{err}</div>}

          {urls.length === 0 ? (
            <div className="text-center text-[#8a8a8a] py-10 text-sm">
              {lang === "ar" ? "لا توجد صور." : "No photos."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="media-grid">
              {urls.map((u, i) => (
                <div key={`${u}-${i}`} className={`surface rounded-xl overflow-hidden ${i === 0 ? "ring-2 ring-[#c9a55c]" : ""}`} data-testid={`media-item-${i}`}>
                  <div className="relative">
                    <img src={u} alt="" className="w-full h-28 object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                    <span className="absolute top-1 start-1 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white font-mono">
                      {i + 1}
                    </span>
                    {i === 0 && (
                      <span className="absolute top-1 end-1 text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: "#c9a55c", color: "#0a0a0a" }}>
                        {lang === "ar" ? "الرئيسية" : "Primary"}
                      </span>
                    )}
                  </div>
                  <div className="p-2 flex flex-wrap gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0}
                            className="btn-ghost text-[10px] px-1.5 py-1 disabled:opacity-40"
                            data-testid={`media-up-${i}`}>
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === urls.length - 1}
                            className="btn-ghost text-[10px] px-1.5 py-1 disabled:opacity-40"
                            data-testid={`media-down-${i}`}>
                      <ArrowDown className="w-3 h-3" />
                    </button>
                    {i !== 0 && (
                      <button onClick={() => setPrimary(i)}
                              className="btn-ghost text-[10px] px-2 py-1"
                              data-testid={`media-set-primary-${i}`}>
                        {lang === "ar" ? "تعيين كرئيسية" : "Set primary"}
                      </button>
                    )}
                    <button onClick={() => remove(i)}
                            className="btn-ghost text-[10px] px-2 py-1 text-red-700 border-red-300"
                            data-testid={`media-remove-${i}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-[#0a0a0a]/[0.08] sticky bottom-0 bg-[#f1ecdf]">
          <button onClick={reset} disabled={!dirty || saving}
                  className="btn-ghost text-xs disabled:opacity-40" data-testid="media-reset">
            <RefreshCw className="w-3 h-3" /> {lang === "ar" ? "استرجاع" : "Reset"}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost text-xs" data-testid="media-cancel">
              {lang === "ar" ? "إلغاء" : "Cancel"}
            </button>
            <button onClick={save} disabled={saving || !dirty}
                    className="btn-primary text-xs disabled:opacity-50" data-testid="media-save">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {lang === "ar" ? "حفظ الترتيب" : "Save order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DisputesTabPlaceholder() { return null; }
// DisputesTab now lives in ./admin/DisputesTab.jsx (full state machine + mediation tools)

function MoyasarHealthCard() {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const probe = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await apiClient.get("/payments/moyasar/health");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "probe failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { probe(); }, []);

  const dot = (ok) => (
    <span className="inline-block w-2 h-2 rounded-full me-2 align-middle"
      style={{ background: ok ? "#22c55e" : "#ef4444" }} />
  );

  return (
    <div className="surface rounded-xl p-4 mb-5" data-testid="moyasar-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="font-display text-lg">
          {lang === "ar" ? "تشخيص بوابة ميسر" : "Moyasar gateway diagnostics"}
        </div>
        <button
          onClick={probe} disabled={loading}
          className="text-xs rounded-full px-3 py-1.5 font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] disabled:opacity-50"
          data-testid="moyasar-health-refresh"
        >
          {loading ? (lang === "ar" ? "…" : "…") : (lang === "ar" ? "تحديث" : "Refresh")}
        </button>
      </div>
      {err && <div className="text-sm text-red-700">{err}</div>}
      {data && (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
          <div>{dot(data.configured)}<b>{lang === "ar" ? "مُهيّأة" : "Configured"}:</b> {String(data.configured)}</div>
          <div>{dot(data.moyasar_reachable)}<b>{lang === "ar" ? "الاتصال بميسر" : "Moyasar reachable"}:</b> {String(data.moyasar_reachable)}</div>
          <div>{dot(data.publishable_key_type === "live")}<b>Publishable:</b> {data.publishable_key_type} <span className="text-[#8a8a8a]" dir="ltr">{data.publishable_key_prefix}</span></div>
          <div>{dot(data.secret_key_type === "live")}<b>Secret:</b> {data.secret_key_type} <span className="text-[#8a8a8a]" dir="ltr">{data.secret_key_prefix}</span></div>
          <div>{dot(data.secret_key_valid)}<b>{lang === "ar" ? "المفتاح صالح" : "Secret key valid"}:</b> {String(data.secret_key_valid)}</div>
          <div>{dot(data.webhook_configured)}<b>Webhook:</b> {String(data.webhook_configured)}</div>
          <div className="sm:col-span-2 mt-1">
            <b>{lang === "ar" ? "أنواع المعاملات المرصودة" : "Observed source types"}:</b>{" "}
            {(data.observed_source_types || []).length
              ? data.observed_source_types.map((t) => (
                  <span key={t} className="pill text-[10px] pill-accent me-1" dir="ltr">{t}</span>
                ))
              : <span className="text-[#8a8a8a]">{lang === "ar" ? "لا توجد معاملات بعد" : "no payments yet"}</span>}
          </div>
          {(data.warnings || []).length > 0 && (
            <div className="sm:col-span-2 mt-2 p-2.5 rounded-lg bg-[#fff7ed] border border-[#fdba74] text-[#9a3412]">
              <b>{lang === "ar" ? "تنبيهات" : "Warnings"}:</b>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {data.last_error && (
            <div className="sm:col-span-2 mt-2 p-2.5 rounded-lg bg-[#fef2f2] border border-[#fca5a5] text-[#7f1d1d] font-mono text-[10px] whitespace-pre-wrap">
              {typeof data.last_error === "string" ? data.last_error : JSON.stringify(data.last_error, null, 2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PaymentsTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const load = () => apiClient.get("/admin/payments").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-payments">
      <MoyasarHealthCard />
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-display text-2xl">{lang === "ar" ? "المدفوعات" : "Payments"} ({items.length})</h2>
      </div>
      <PaymentsCleanupPanel ar={lang === "ar"} onDone={load} />
      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
            <tr><th className="p-3 text-start">Date</th><th className="p-3 text-start">{lang === "ar" ? "المستخدم" : "User"}</th><th className="p-3 text-start">Package</th><th className="p-3 text-end">Amount</th><th className="p-3 text-start">Status</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="5" className="p-12 text-center text-[#6b6b6b]">{lang === "ar" ? "لا توجد مدفوعات بعد." : "No payments yet."}</td></tr>
            ) : items.map((p) => (
              <tr key={`${p.source}-${p.id}`} className="border-t border-[#0a0a0a]/[0.06]">
                <td className="p-3 text-[#3a3a3a]">{new Date(p.created_at).toLocaleString()}</td>
                <td className="p-3">{p.user_name || p.user_email || (lang === "ar" ? "غير محدد" : "Unidentified")}</td>
                <td className="p-3">{p.description || p.sku || p.purpose}</td>
                <td className="p-3 text-end">{formatSAR(p.amount_sar)} {(p.currency || "SAR").toUpperCase()}</td>
                <td className="p-3"><span className={`pill text-[10px] ${p.status === "paid" ? "pill-accent" : ""}`}>{PAY_STATUS[p.status] ? PAY_STATUS[p.status][lang === "ar" ? "ar" : "en"] : p.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeadsBlock({ title, items, lang }) {
  return (
    <div>
      <h3 className="font-display text-lg mb-3">{title} ({items.length})</h3>
      <div className="space-y-2">
        {items.length === 0 ? <div className="text-[#6b6b6b] text-sm">{lang === "ar" ? "لا شيء بعد" : "Nothing yet"}</div> :
          items.slice(0, 10).map((l) => (
            <div key={l.id} className="surface rounded-lg p-3 text-sm flex justify-between gap-3 flex-wrap">
              <div>
                <div className="font-medium">{l.name || `${l.vehicle_year} ${l.vehicle_make} ${l.vehicle_model}`}</div>
                <div className="text-xs text-[#6b6b6b]">{l.email || l.location} · {l.phone}</div>
              </div>
              <div className="text-xs text-[#8a8a8a]">{new Date(l.created_at).toLocaleDateString()}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

function LeadsTab() {
  const { lang } = useI18n();
  const [data, setData] = useState({ financing: [], insurance: [], inspection: [] });
  useEffect(() => { apiClient.get("/admin/leads").then(({ data }) => setData(data)); }, []);
  return (
    <div data-testid="admin-leads" className="grid lg:grid-cols-3 gap-6">
      <LeadsBlock title={lang === "ar" ? "تمويل" : "Financing"} items={data.financing} lang={lang} />
      <LeadsBlock title={lang === "ar" ? "تأمين" : "Insurance"} items={data.insurance} lang={lang} />
      <LeadsBlock title={lang === "ar" ? "فحص" : "Inspection"} items={data.inspection} lang={lang} />
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const openImportsTab = React.useCallback(() => setParams({ tab: "imports" }), [setParams]);
  // Honour ?tab=X from the URL so deep-links from anywhere in the app land on
  // the right admin section.
  const initial = params.get("tab") || "overview";
  const [tab, setTab] = useState(initial);
  useEffect(() => {
    const t = params.get("tab");
    if (t && t !== tab) setTab(t);
  }, [params, tab]);
  const switchTab = (id) => {
    setTab(id);
    const sp = new URLSearchParams(params);
    sp.set("tab", id);
    setParams(sp, { replace: true });
  };

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (!["super_admin", "admin"].includes(user.role)) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center">
        <Shield className="w-12 h-12 mx-auto text-[#a8893e] mb-4" />
        <h1 className="font-display text-2xl">{lang === "ar" ? "هذه الصفحة للمشرفين فقط" : "Admin access only"}</h1>
      </div>
    );
  }

  const isSuper = user.role === "super_admin";
  // iter182 — respect fine-grained perms too. super_admin bypasses.
  const visibleTabs = TABS.filter(t => {
    if (t.superOnly && !isSuper) return false;
    if (!t.perm) return true;
    return isSuper || hasPerm(user, t.perm);
  });

  const renderTab = () => {
    switch (tab) {
      case "overview": return <Overview />;
      case "analytics": return <AnalyticsTab />;
      case "users": return <UsersTab />;
      case "vehicles": return <VehiclesTab />;
      case "reports": return <ReportsTab />;
      case "kyc": return <KycTab />;
      case "biz_verif": return <BusinessVerificationTab />;
      case "disputes": return <DisputesTab />;
      case "messages": return <MessagesTab />;
      case "cartal_channel": return <CartalChannelTab />;
      case "faq": return <FaqAdminTab />;
      case "broadcasts": return <BroadcastsTab />;
      case "ads": return <AdCampaignsTab />;
      case "partners": return <PartnersTab />;
      case "other_services": return <OtherServicesTab />;
      case "packages": return <PackagesTab />;
      case "private_offers": return <PrivateOffersTab />;
      case "campaigns": return <CampaignsManagerTab />;
      case "offers_subs": return <OffersSubscriptionsTab />;
      case "verif_audit": return <VerificationAuditLogTab />;
      case "banners": return <BannersTab />;
      case "makes": return <MakesTab />;
      case "payments": return <PaymentsTab />;
      case "payouts": return <PayoutsTab />;
      case "leads": return <LeadsTab />;
      case "admins": return <AdminsTab />;
      case "settings": return <SettingsTab />;
      case "backups": return <BackupsTab />;
      case "audit": return <AuditTab />;
      case "archive_individuals": return <IndividualArchiveTab />;
      case "archive_businesses": return <BusinessArchiveTab />;
      case "geo_spread": return <GeoSpreadTab />;
      case "heavy_taxonomy": return <HeavyTaxonomyTab />;
      case "imports": return <ImportAuthorizationsTab />;
      default: return null;
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-10" data-testid="admin-page">
      {hasPerm(user, "listings.imports.manage") && <ImportRequestAlert ar={lang === "ar"} onOpen={openImportsTab} />}
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-5 h-5 text-[#8a6f2e]" />
        <div className="overline overline-gold">CARTAL ADMIN</div>
      </div>
      <h1 className="font-display text-4xl tracking-[-0.02em] font-medium mb-8">
        {lang === "ar" ? "لوحة تحكم الإدارة الشاملة" : "Comprehensive admin panel"}
      </h1>

      <div className="flex gap-1 mb-8 border-b border-[#0a0a0a]/[0.08] overflow-x-auto">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`px-4 py-3 text-sm inline-flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-[#c9a55c] text-[#0a0a0a] font-medium" : "border-transparent text-[#6b6b6b] hover:text-[#0a0a0a]"}`}
              data-testid={`admin-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {lang === "ar" ? t.ar : t.en}
            </button>
          );
        })}
      </div>

      {renderTab()}
    </div>
  );
}
